import {
  createLibraryRecord,
  deleteLibraryRecord,
  fetchLibraryFields,
  fetchLibraryRecord,
  fetchLibraryRecords,
  patchLibraryRecord,
} from '@/features/metadata-library/api/metadataLibrary';
import {
  METADATA_LIBRARY_FIELDS_STALE_TIME,
  METADATA_LIBRARY_PAGE_LIMIT,
  METADATA_LIBRARY_STALE_TIME,
} from '@/features/metadata-library/config/constants';
import { operatorKeys } from '@/features/metadata-library/config/operatorKeys';
import { operatorFormDefinition } from '@/features/metadata-library/lib/operatorForm';
import type {
  LibraryCreateInputMap,
  OperatorSnapshot,
} from '@/features/metadata-library/types/libraryRequests';
import type { LibraryRecordsPage } from '@/features/metadata-library/types/metadataLibrary';
import type { SupportedLocale } from '@/shared/types/i18n';
import {
  type QueryClient,
  type QueryFunctionContext,
  type QueryKey,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export function operatorFieldsQuery(locale: SupportedLocale) {
  return queryOptions({
    queryKey: operatorKeys.fields(locale),
    queryFn: ({ signal }) => fetchLibraryFields('operators', locale, { signal }),
    staleTime: METADATA_LIBRARY_FIELDS_STALE_TIME,
  });
}

export function useOperatorFields(locale: SupportedLocale) {
  return useQuery({ ...operatorFieldsQuery(locale), select: operatorFormDefinition });
}

export function operatorRecordsQuery(q?: string, organizationIds?: readonly string[]) {
  const term = q?.trim() || undefined;

  return {
    queryKey: operatorKeys.recordsList(term, organizationIds),
    queryFn: ({ pageParam, signal }: QueryFunctionContext<QueryKey, string | undefined>) =>
      fetchLibraryRecords(
        'operators',
        {
          q: term,
          organizationId: organizationIds,
          cursor: pageParam,
          limit: METADATA_LIBRARY_PAGE_LIMIT,
        },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: LibraryRecordsPage) =>
      last.items.length === 0 ? undefined : (last.nextCursor ?? undefined),
    staleTime: METADATA_LIBRARY_STALE_TIME,
  };
}

export function operatorRecordQuery(recordId: string) {
  return {
    queryKey: operatorKeys.record(recordId),
    queryFn: ({ signal }: QueryFunctionContext) =>
      fetchLibraryRecord('operators', recordId, { signal }),
    enabled: Boolean(recordId),
    staleTime: METADATA_LIBRARY_STALE_TIME,
    retry: false,
  };
}

export function useOperatorRecord(recordId: string) {
  return useQuery(operatorRecordQuery(recordId));
}

export function useCreateOperator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LibraryCreateInputMap['operators']) =>
      createLibraryRecord('operators', body),
    onSuccess: (record) => {
      queryClient.setQueryData(operatorKeys.record(record.operatorId), record);
      void queryClient.invalidateQueries({ queryKey: operatorKeys.records });
    },
  });
}

export function usePatchOperator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, body }: { recordId: string; body: OperatorSnapshot }) =>
      patchLibraryRecord('operators', recordId, body),
    onSuccess: (record, variables) => {
      queryClient.setQueryData(operatorKeys.record(variables.recordId), record);
      void queryClient.invalidateQueries({ queryKey: operatorKeys.records });
    },
  });
}

export function operatorDeleteMutation(queryClient: QueryClient) {
  return {
    mutationFn: (recordId: string) => deleteLibraryRecord('operators', recordId),
    onSuccess: (_result: unknown, recordId: string) => {
      void queryClient.invalidateQueries({ queryKey: operatorKeys.record(recordId), exact: true });
      void queryClient.invalidateQueries({ queryKey: operatorKeys.records });
    },
  };
}
